import type RouterService from '@ember/routing/router-service';
import type Transition from '@ember/routing/transition';
import type { Transition as TransitionWithTo } from 'lumen/services/transition';

export interface ControllerWithUnsavedChanges {
  discardChanges: () => void;
  hasUnsavedChanges: boolean;
  showDiscardChangesAlert: () => Promise<boolean>;
}

export interface TransitionHandler {
  cleanup: () => void;
}

/**
 * Utility function to set up routeWillChange and beforeunload handlers for unsaved changes.
 *
 * @example
 * import { setupUnsavedChangesTransitionHandler, type TransitionHandler } from 'lumen/utils/unsaved-changes-transition-handlers';
 *
 * export default class MyRoute extends Route {
 *   @service router;
 *
 *   #transitionHandler?: TransitionHandler;
 *
 *   setupController(controller) {
 *     super.setupController(...arguments);
 *     this.#transitionHandler = setupUnsavedChangesTransitionHandler(this.router, controller, 'example.route');
 *   }
 *
 *   resetController(controller) {
 *     super.resetController(...arguments);
 *     if (this.#transitionHandler) {
 *       this.#transitionHandler.cleanup();
 *       this.#transitionHandler = undefined;
 *    }
 *   }
 * }
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function setupUnsavedChangesTransitionHandler(
  router: RouterService,
  controller: ControllerWithUnsavedChanges,
  routeName: string,
) {
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const transitionHandler = async (transition: Transition) => {
    if (
      (transition as TransitionWithTo).to.name === routeName ||
      !controller.hasUnsavedChanges
    ) {
      return;
    }

    transition.abort();

    const shouldDiscardChanges = await controller.showDiscardChangesAlert();

    if (shouldDiscardChanges) {
      controller.discardChanges();
      transition.retry();
    }
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const beforeUnloadHandler = (event: BeforeUnloadEvent) => {
    if (controller.hasUnsavedChanges) {
      event.preventDefault();
      event.returnValue = '';
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  router.on('routeWillChange', transitionHandler);
  window.addEventListener('beforeunload', beforeUnloadHandler);

  return {
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    cleanup: () => {
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      router.off('routeWillChange', transitionHandler);
      window.removeEventListener('beforeunload', beforeUnloadHandler);
    },
  };
}
